import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, getApiErrorMessage } from "@/lib/api";
import { CheckCircle2, ArrowRight } from "lucide-react";
import PasswordStrength from "@/components/PasswordStrength";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) { setError(getApiErrorMessage(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F2] px-6 py-10">
      <div className="w-full max-w-md bg-white border border-[#E8E6E1] rounded-3xl p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="reset-password-page">
        <h1 className="font-heading text-3xl font-semibold text-[#2A332C]">Choose a new password</h1>
        <p className="text-sm text-[#5C6660] mt-2 mb-6">Pick something strong — at least 8 characters with a mix of letters and numbers.</p>

        {success ? (
          <div className="text-center py-6" data-testid="reset-success">
            <div className="w-12 h-12 rounded-full bg-[#E6F0EB] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={22} className="text-[#2A4D3E]" />
            </div>
            <div className="font-medium text-[#2A332C]">Password updated</div>
            <p className="text-sm text-[#5C6660] mt-2">Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#5C6660] mb-1.5">New password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input"
                className="w-full border border-[#E8E6E1] rounded-xl px-4 py-3 bg-[#FDFCFB] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]" />
              <PasswordStrength password={password} />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5C6660] mb-1.5">Confirm password</label>
              <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-input"
                className="w-full border border-[#E8E6E1] rounded-xl px-4 py-3 bg-[#FDFCFB] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]" />
            </div>
            {error && <div className="text-sm text-[#C0392B] bg-[#FDEDEC] border border-[#F5D6D2] rounded-xl px-4 py-3" data-testid="reset-error">{error}</div>}
            <button type="submit" disabled={loading || !token} data-testid="reset-submit"
              className="w-full bg-[#2A4D3E] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1E3B2F] disabled:opacity-50 flex items-center justify-center gap-2 group">
              {loading ? "Updating…" : (<>Update password <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>)}
            </button>
            {!token && <div className="text-sm text-[#C0392B] text-center">Missing reset token. Use the link from your email.</div>}
          </form>
        )}
        <div className="mt-6 text-center">
          <Link to="/login" className="text-xs text-[#5C6660] hover:text-[#2A4D3E]">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
